/* eslint-disable array-bracket-newline */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNetwork } from 'wagmi';
import { IAdditionalPoints } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import Button from '../../../molecules/Button';
// import { onCopyAddress } from '../../../helpers/addressUtils';
import { IStore } from '../../../reducers';
import { authTokenSelector } from '../../../selectors/auth';
import Loader from '../../../components/common/Loader';
import { toFixed2 } from '../../../components/DexTrades/formatters';
import { actionCreateNotification } from '../../../actions/notifications';
import UseSecretCode from './UseSecretCode';
import UseTwitter from './UseTwitter';
import { CexAPI, UserAPI } from '../../../store/createStore';
import { actionRefreshCompetition } from '../../../actions/competition';
import { onCopyAddress } from '../../../helpers/addressUtils';

import inviteUser from '../../../assets/images/add_user.svg';

import './styles.scss';

const AdditionalPoints = () => {
  const authToken = useSelector(authTokenSelector);
  const loggedIn = useSelector((state: IStore) => state.auth.loggedIn);

  const { exchange } = useParams();

  // const [code, setCode] = useState(false);
  const [additionalPoints, setAdditionalPoints] =
    useState<IAdditionalPoints | null>(null);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const { chain } = useNetwork();

  const internalNetwork = chain?.network;
  let network = 'EVM';
  if (internalNetwork == 'ELROND') {
    network = 'ELROND';
  } else if (internalNetwork == 'TRON') {
    network = 'TRON';
  } else if (internalNetwork == 'TON') {
    network = 'TON';
  }

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    if (loggedIn) {
      CexAPI.fetchAdditionalPoints(
        exchange || 'binance',
        authToken!,
        controller
      ).then((data) => {
        setAdditionalPoints(data);
        setLoading(false);
      });
    } else {
      setAdditionalPoints(null);
      setLoading(false);
    }
    return () => {
      controller.abort();
    };
  }, [authToken, exchange, loggedIn]);

  const onClaim = async () => {
    const data = await CexAPI.claimAdditionalPoints(
      exchange || 'binance',
      authToken!
    );

    if (data && typeof data !== 'string' && data.claimedPoints) {
      dispatch(actionRefreshCompetition());
      dispatch(
        actionCreateNotification({
          kind: 'info',
          message: `You've successfully claimed ${data.claimedPoints} points`,
        })
      );
    } else {
      dispatch(
        actionCreateNotification({
          kind: 'error',
          message: 'Sorry, but we could not claim your points',
        })
      );
    }
  };

  const onInvite = async () => {
    const resp = await UserAPI.getReferral(network, authToken!);
    if (resp) {
      onCopyAddress(
        window.location.origin + '/?referrall=' + resp.referral_code,
        undefined,
        'Referral link was copied to clipboard'
      );
    } else {
      dispatch(
        actionCreateNotification({
          kind: 'error',
          message: 'Could not fetch your referral link',
        })
      );
    }
  };

  /**
   * Task logic and decorators
   */
  const firstTaskValue = additionalPoints?.VolumeTopOrder24 || 0;
  const firstTaskCompleted = firstTaskValue >= 100;
  const firstTask = `${toFixed2(firstTaskValue)}/100`;

  const secondTaskValue = additionalPoints?.Volume24 || 0;
  const secondTask = `${toFixed2(secondTaskValue)}/1000`;
  const secondTaskCompleted = secondTaskValue >= 1000;

  const thirdTaskValue = additionalPoints?.CountVarietyPairs24 || 0;
  const thirdTask = `${thirdTaskValue}/3`;
  const thirdTaskCompleted = thirdTaskValue >= 3;

  const canClaim = !!(
    firstTaskCompleted &&
    secondTaskCompleted &&
    thirdTaskCompleted
  );

  return (
    <div className='points'>
      <Text size={14} className='points__header'>
        Get additional points
      </Text>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : (
        <div className='points__body'>
          <div className='points__body__item'>
            <Text size={12}>Daily 100$ trade</Text>
            <Text size={12} className='text--grey-400'>
              {firstTaskCompleted ? (
                <div className='points__body__item__icon points__body__item__icon--true icon-check-circle' />
              ) : (
                firstTask
              )}
            </Text>
          </div>
          <div className='points__body__item'>
            <Text size={12}>Daily 1000$ volume</Text>
            <Text size={12} className='text--grey-400'>
              {secondTaskCompleted ? (
                <div className='points__body__item__icon points__body__item__icon--true icon-check-circle' />
              ) : (
                secondTask
              )}
            </Text>
          </div>
          <div className='points__body__item'>
            <Text size={12}>Share on twitter</Text>
            <UseTwitter exchange={exchange} authToken={authToken!} />
          </div>
          <div className='points__body__item'>
            <Text size={12}>Make 1 trade on 3 pairs</Text>
            <Text size={12} className='text--grey-400'>
              {thirdTaskCompleted ? (
                <div className='points__body__item__icon points__body__item__icon--true icon-check-circle' />
              ) : (
                thirdTask
              )}
            </Text>
          </div>
          <div className='points__body__item'>
            <Text size={12}>Use secret code</Text>
            <Text size={12} className='text--grey-400'>
              <UseSecretCode
                exchange={exchange}
                authToken={authToken!}
                loggedIn={loggedIn}
                onSuccess={() => dispatch(actionRefreshCompetition())}
                onNotify={(data: any) =>
                  dispatch(actionCreateNotification(data))
                }
              />
            </Text>
          </div>
          <div className='points__body__invite_btn'>
            <Button
              size='s'
              disabled={!canClaim}
              variant='secondary'
              action={onClaim}
              text={`💰  Claim ${
                additionalPoints?.AdditionalPoint || 0
              } additional points`}
            />
            <Button
              left_image={inviteUser}
              size='s'
              variant='secondary'
              action={onInvite}
              text='Invite a friend'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdditionalPoints;
